import { batch } from "react-redux";
import { setQrCode } from "slices/accessSlice";
import { setAppState } from "slices/appStateSlice";
import { setDishTypes, setDishTypesLighting } from "slices/dishesSlice";
import { setDoorLockState, setDoorState, setHasDoorBeenOpenedDuringOrder, setLockState } from "slices/doorSlice";
import { setFridgeState } from "slices/fridgeInfoSlice";
import { setLightingMode } from "slices/fridgeLightingSlice";
import { isActiveOrderSlot, setTransaction } from "slices/transactionSlice";
import type { AppDispatch, RootState } from "store";
import { type DoorState, type Refill, TransactionMode, TransactionState } from "types";
import { AppState, type LocalDishType, type LocalFridgeState } from "types/mainTypes";
import { logToSentry } from "utils/logger";
import { getRefillTransitions } from "utils/refillTransitions";
import { isLightingEchoFrame } from "utils/utils";

export type ApplyFridgeFrameParams = {
	// The FRIDGE_SUBSCRIPTION frame with `__typename` already stripped (a SPARSE delta — only changed
	// fields are set, the rest are null; NEVER a full authoritative snapshot).
	cleanedData: LocalFridgeState;
	dispatch: AppDispatch;
	// Read synchronously between dispatches (dispatch is synchronous, so getState() is never stale even
	// when rapid pushes arrive before React re-renders — the reason the caller passes the store, not a
	// closed-over selector value).
	getState: () => RootState;
	trackDoorStateChange: (doorState: DoorState) => void;
	trackRefillStarted: (refill: Refill) => void;
	trackRefillFinished: (refill: Refill) => void;
	getUnknownDishTypes: (dishTypes: LocalDishType[]) => void;
	idleTimer: { start: () => void };
	lastTrackedDoorStateRef: { current: DoorState | null };
	fridgeId: string;
};

/**
 * Applies one FRIDGE_SUBSCRIPTION frame to Redux. Extracted verbatim from DeviceContext's `useSubscription`
 * onData so the sparse-delta fan-out — the seam where the 1.14.x lighting/refill regressions lived — is
 * unit-testable in isolation. Pure w.r.t. its params: it only dispatches and reads the injected store.
 */
export const applyFridgeFrame = ({
	cleanedData,
	dispatch,
	getState,
	trackDoorStateChange,
	trackRefillStarted,
	trackRefillFinished,
	getUnknownDishTypes,
	idleTimer,
	lastTrackedDoorStateRef,
	fridgeId,
}: ApplyFridgeFrameParams): void => {
	const transaction = cleanedData.transaction;
	const isLastOrderFinished = transaction?.orders?.last?.state === TransactionState.Finished;
	const hasDishTypes = cleanedData?.dishes?.types;
	// A spotlight TOGGLE echo is a SPARSE single-type frame (lighting.mode set, `dishes: null`). It
	// must NOT go through the snapshot reconcile, which would empty every still-stocked type the echo
	// omits — the master Meal List wipe on detail-open. Route it to a lighting-only update instead.
	const isLightingEcho = isLightingEchoFrame(hasDishTypes);

	batch(() => {
		if (cleanedData.state != null) {
			dispatch(setFridgeState(cleanedData.state));
		}

		const hasDoorStateChange = cleanedData.door?.lock?.state;

		if (cleanedData.access?.qrCode?.code) {
			dispatch(setQrCode(cleanedData.access.qrCode.code));
		}
		if (hasDoorStateChange) {
			if (cleanedData.door.state && cleanedData.door.state !== lastTrackedDoorStateRef.current) {
				trackDoorStateChange(cleanedData.door.state);
				lastTrackedDoorStateRef.current = cleanedData.door.state;
			}
			dispatch(setDoorLockState(cleanedData.door));
		} else if (cleanedData?.door?.state) {
			if (cleanedData.door.state !== lastTrackedDoorStateRef.current) {
				trackDoorStateChange(cleanedData.door.state);
				lastTrackedDoorStateRef.current = cleanedData.door.state;
			}
			dispatch(setDoorState(cleanedData.door.state));
		} else if (cleanedData?.door?.lock?.state) {
			dispatch(setLockState(cleanedData.door.lock?.state));
		}

		if (cleanedData.lighting?.mode != null) {
			dispatch(setLightingMode(cleanedData.lighting.mode));
		}

		if (!isLightingEcho && cleanedData.dishes?.types) {
			getUnknownDishTypes(cleanedData.dishes.types);
		}

		// Hoisted so the dish-type reconcile below can fire at refill finish too (the BE sends
		// `dishes.types: null` when the whole fridge is emptied, so we need this to clear the last type).
		let isRefillFinished = false;
		if (transaction?.orders?.last || transaction?.refills?.last) {
			const prevSlot = getState().transaction.slot;
			dispatch(
				setTransaction({
					order: transaction.orders?.last ?? null,
					refill: transaction.refills?.last ?? null,
				}),
			);
			const nextSlot = getState().transaction.slot;
			// A different transaction id taking the slot means a new flow starts: reset the door flag
			// (covers cross-kind takeovers too). hasDoorBeenOpenedDuringOrder is shared between order
			// and refill flows because useOrderSteps drives both via selectActiveLocalTransaction.
			if (prevSlot && nextSlot && prevSlot.transaction.id !== nextSlot.transaction.id) {
				dispatch(setHasDoorBeenOpenedDuringOrder(false));
			}
			if (nextSlot?.kind === "refill") {
				const prevRefill = prevSlot?.kind === "refill" ? prevSlot.transaction : null;
				const { isNewRefill, isRefillFinished: refillFinished } = getRefillTransitions(
					prevRefill,
					nextSlot.transaction,
				);
				isRefillFinished = refillFinished;
				// Finished guard: reconnect echoes of old finished refills must not emit a spurious "started".
				if (isNewRefill && nextSlot.transaction.state !== TransactionState.Finished) {
					logToSentry(`Refill started on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
					trackRefillStarted(nextSlot.transaction);
				}
				if (refillFinished) {
					logToSentry(`Refill finished on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
					trackRefillFinished(nextSlot.transaction);
					if (nextSlot.transaction.mode === TransactionMode.Remote) {
						// The end of a remote refill counts as fresh presence (mirrors finishOrder):
						// re-arm the inactive countdown and wake appState so the fridge and screen
						// stay on for a FULL inactive timeout before the normal idle edge dims them.
						// Remote refills have no OrderStep5/finishOrder, so nothing else provides
						// this wake — without it the BE-lit fridge stayed ACTIVE forever after a
						// remote refill (HW capture 11. 6. 2026).
						idleTimer.start();
						dispatch(setAppState(AppState.ACTIVE));
					}
				}
			}
		}

		if (isLightingEcho) {
			// Lighting-only echo: update per-type lighting.mode without touching the type set or any
			// `dishes` array, so the dish types the sparse frame omits stay on the Meal List.
			dispatch(setDishTypesLighting({ dishTypes: hasDishTypes ?? [] }));
		} else if (hasDishTypes || isLastOrderFinished || isRefillFinished) {
			// `dishes.types` carries TWO different frame semantics. During an ACTIVE order the BE streams
			// SPARSE single-type deltas (absence = unchanged), so the reducer must merge only — emptying
			// absent types collapsed availableDishTypeIds to the one delta'd type (Meal List + lighting
			// collapse; 1.14.x regression of 4af3a56). Refills, order-finish and browse send the FULL
			// authoritative snapshot (absence = the last dish was taken → drop it), so they keep the
			// destructive reconcile. When the whole fridge is emptied the BE sends `dishes.types: null`, so
			// on a finished order/refill we fall back to an empty array to clear the final dish type too.
			dispatch(
				setDishTypes({
					dishTypes: hasDishTypes ?? [],
					reconcileRemovals: !isActiveOrderSlot(getState().transaction.slot),
				}),
			);
		}
	});
};
